import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import polyline from '@mapbox/polyline';
import 'mapbox-gl/dist/mapbox-gl.css';

const MAPBOX_ACCESS_TOKEN = 'pk.eyJ1IjoiZGFuaWVsa2FsZG9yIiwiYSI6ImNtYjRyNTYzczA1emcya3FwdzhoMmhmdGIifQ.qkeEKxO6j5ugrpbdC06grA';

if (typeof mapboxgl !== 'undefined') {
    mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;
}

const DEFAULT_COARSE_BEARING_COUNT = 12;
const DEFAULT_STEP_DELAY_MS = 250;
const ROUTE_LAYER_ID = 'route-2d';
const ROUTE_SOURCE_ID = 'route-2d';
const WORLD_SIZE = 512;

const FOOTPRINT_REWARD_WEIGHT = 4200;
const FOOTPRINT_REWARD_CAP = 0.78;
const CENTER_PENALTY_WEIGHT = 400;
const OUT_OF_BOUNDS_PENALTY = 100000;
// Ignore sub-pixel overruns caused by floating-point drift (kept in sync with
// functions/src/map_2d_bearing_search.ts).
const OUT_OF_BOUNDS_EPSILON = 0.001;
// Penalty for routes that pass through the logo/stats overlay regions. Weight applies to
// `logoHitRatio + statsHitRatio`, each in [0, 1]. At combined 100% hit the penalty roughly
// cancels a max area reward, so a route that fits perfectly but cuts through the legend
// ranks below a route that fits slightly worse but avoids overlays.
const OVERLAY_PENALTY_WEIGHT = 3000;

// Overlay boxes in capture pixels. Top-left (logo) + bottom-right (stats legend).
const DEFAULT_LOGO_OVERLAY_WIDTH = 280;
const DEFAULT_LOGO_OVERLAY_HEIGHT = 280;
const DEFAULT_STATS_OVERLAY_WIDTH = 420;
const DEFAULT_STATS_OVERLAY_HEIGHT = 280;
const DEFAULT_PADDING = 100;

const normalizeBearing = (bearing) => ((bearing % 360) + 360) % 360;

const lngLatToWorld = (lng, lat) => {
    const clampedLat = Math.max(-85.051129, Math.min(85.051129, lat));
    const x = (lng + 180) / 360;
    const sinLat = Math.sin((clampedLat * Math.PI) / 180);
    const y = 0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI);
    return { x, y };
};

const worldToLngLat = (x, y) => {
    const lng = x * 360 - 180;
    const n = Math.PI - 2 * Math.PI * y;
    const lat = (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
    return { lng, lat };
};

const projectLngLat = (lng, lat, centerLng, centerLat, zoom, bearingDeg, viewportWidth, viewportHeight) => {
    const point = lngLatToWorld(lng, lat);
    const center = lngLatToWorld(centerLng, centerLat);
    const bearingRad = (bearingDeg * Math.PI) / 180;
    const cos = Math.cos(-bearingRad);
    const sin = Math.sin(-bearingRad);
    const dx = point.x - center.x;
    const dy = point.y - center.y;
    const scale = WORLD_SIZE * Math.pow(2, zoom);
    return {
        x: viewportWidth / 2 + (dx * cos - dy * sin) * scale,
        y: viewportHeight / 2 + (dx * sin + dy * cos) * scale,
    };
};

const buildFrame = (frameWidth, frameHeight, paddingObject) => ({
    width: frameWidth,
    height: frameHeight,
    padTop: paddingObject.top,
    padBottom: paddingObject.bottom,
    padLeft: paddingObject.left,
    padRight: paddingObject.right,
});

const fitCameraToRoute = (worldPoints, bearingDeg, frame) => {
    if (!worldPoints.length) return null;

    const bearingRad = (bearingDeg * Math.PI) / 180;
    const cosB = Math.cos(-bearingRad);
    const sinB = Math.sin(-bearingRad);

    let minRx = Infinity;
    let maxRx = -Infinity;
    let minRy = Infinity;
    let maxRy = -Infinity;

    worldPoints.forEach((point) => {
        const rx = point.x * cosB - point.y * sinB;
        const ry = point.x * sinB + point.y * cosB;
        if (rx < minRx) minRx = rx;
        if (rx > maxRx) maxRx = rx;
        if (ry < minRy) minRy = ry;
        if (ry > maxRy) maxRy = ry;
    });

    const rotatedBBoxWidth = Math.max(1e-12, maxRx - minRx);
    const rotatedBBoxHeight = Math.max(1e-12, maxRy - minRy);
    const rotatedCenterX = (minRx + maxRx) / 2;
    const rotatedCenterY = (minRy + maxRy) / 2;

    const availableW = Math.max(1, frame.width - frame.padLeft - frame.padRight);
    const availableH = Math.max(1, frame.height - frame.padTop - frame.padBottom);
    const scale = Math.min(
        availableW / (rotatedBBoxWidth * WORLD_SIZE),
        availableH / (rotatedBBoxHeight * WORLD_SIZE)
    );
    if (!Number.isFinite(scale) || scale <= 0) return null;

    const zoom = Math.log2(scale);
    const cosU = Math.cos(bearingRad);
    const sinU = Math.sin(bearingRad);
    let worldCenterX = rotatedCenterX * cosU - rotatedCenterY * sinU;
    let worldCenterY = rotatedCenterX * sinU + rotatedCenterY * cosU;

    const safeCenterPxX = frame.padLeft + availableW / 2;
    const safeCenterPxY = frame.padTop + availableH / 2;
    const viewportCenterPxX = frame.width / 2;
    const viewportCenterPxY = frame.height / 2;
    const screenOffsetPxX = safeCenterPxX - viewportCenterPxX;
    const screenOffsetPxY = safeCenterPxY - viewportCenterPxY;

    if (screenOffsetPxX !== 0 || screenOffsetPxY !== 0) {
        const pxToWorld = 1 / (WORLD_SIZE * Math.pow(2, zoom));
        const screenDxPx = -screenOffsetPxX * pxToWorld;
        const screenDyPx = -screenOffsetPxY * pxToWorld;
        const dxWorld = screenDxPx * cosU - screenDyPx * sinU;
        const dyWorld = screenDxPx * sinU + screenDyPx * cosU;
        worldCenterX += dxWorld;
        worldCenterY += dyWorld;
    }

    const { lng, lat } = worldToLngLat(worldCenterX, worldCenterY);
    return { centerLng: lng, centerLat: lat, zoom };
};

const decodeActivityPolyline = (activity, selectedSegment) => {
    const segmentPolylineData = selectedSegment?.segment?.map?.polyline || selectedSegment?.segment?.map?.summary_polyline;
    const polylineString = segmentPolylineData || activity?.map?.summary_polyline || null;
    if (!polylineString) return null;
    try {
        const decoded = polyline.decode(polylineString);
        const validCoords = decoded
            .map((coord) => [coord[1], coord[0]])
            .filter((coord) => !isNaN(coord[0]) && !isNaN(coord[1]) && Math.abs(coord[0]) <= 180 && Math.abs(coord[1]) <= 90);
        return validCoords.length > 0 ? validCoords : null;
    } catch (error) {
        console.error('ActivityMap2DSearch: polyline decode failed', error);
        return null;
    }
};

const waitForAnimationFrames = (count = 1) => new Promise((resolve) => {
    let remaining = count;
    const tick = () => {
        remaining -= 1;
        if (remaining <= 0) resolve();
        else requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
});

const waitForMs = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const waitForIdle = (map, timeoutMs = 3000) => new Promise((resolve) => {
    if (!map) return resolve();
    let settled = false;
    const done = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        map.off('idle', done);
        resolve();
    };
    const timeoutId = setTimeout(done, timeoutMs);
    map.once('idle', done);
});

const resolvePaddingObject = (padding) => {
    if (padding && typeof padding === 'object') {
        const top = Number.isFinite(padding.top) ? padding.top : 0;
        const right = Number.isFinite(padding.right) ? padding.right : 0;
        const bottom = Number.isFinite(padding.bottom) ? padding.bottom : 0;
        const left = Number.isFinite(padding.left) ? padding.left : 0;
        return { top, right, bottom, left };
    }
    const scalar = Number.isFinite(padding) ? padding : 0;
    return { top: scalar, right: scalar, bottom: scalar, left: scalar };
};

const scalePadding = (paddingObject, captureWidth, captureHeight, displayWidth, displayHeight) => {
    const scaleX = Math.max(0.0001, displayWidth) / Math.max(0.0001, captureWidth);
    const scaleY = Math.max(0.0001, displayHeight) / Math.max(0.0001, captureHeight);
    return {
        top: paddingObject.top * scaleY,
        bottom: paddingObject.bottom * scaleY,
        left: paddingObject.left * scaleX,
        right: paddingObject.right * scaleX,
    };
};

const ActivityMap2DSearch = ({
    activity,
    selectedSegment = null,
    width = 1280,
    height = 853,
    displayWidth = 480,
    displayHeight = 320,
    mapStyleUrl = 'mapbox://styles/samuelcollin3333/cmcyoppzj00ds01rf0gjpbt16',
    lineWidth = 5,
    lineColor = '#FC5200',
    lineOpacity = 0.75,
    padding = DEFAULT_PADDING,
    logoOverlayWidth = DEFAULT_LOGO_OVERLAY_WIDTH,
    logoOverlayHeight = DEFAULT_LOGO_OVERLAY_HEIGHT,
    statsOverlayWidth = DEFAULT_STATS_OVERLAY_WIDTH,
    statsOverlayHeight = DEFAULT_STATS_OVERLAY_HEIGHT,
    coarseBearingCount = DEFAULT_COARSE_BEARING_COUNT,
    stepDelayMs = DEFAULT_STEP_DELAY_MS,
    enableDebugCandidates = true,
    onDebugCandidate = null,
    onDebugSummary = null,
    onSearchStart = null,
    onSearchComplete = null,
    onSearchError = null,
}) => {
    const mapContainer = useRef(null);
    const mapRef = useRef(null);
    const [progressText, setProgressText] = useState('Preparing 2D map...');
    const [hasError, setHasError] = useState(false);

    const onDebugCandidateRef = useRef(onDebugCandidate);
    const onDebugSummaryRef = useRef(onDebugSummary);
    const onSearchStartRef = useRef(onSearchStart);
    const onSearchCompleteRef = useRef(onSearchComplete);
    const onSearchErrorRef = useRef(onSearchError);
    const enableDebugCandidatesRef = useRef(enableDebugCandidates);

    useEffect(() => { onDebugCandidateRef.current = onDebugCandidate; }, [onDebugCandidate]);
    useEffect(() => { onDebugSummaryRef.current = onDebugSummary; }, [onDebugSummary]);
    useEffect(() => { onSearchStartRef.current = onSearchStart; }, [onSearchStart]);
    useEffect(() => { onSearchCompleteRef.current = onSearchComplete; }, [onSearchComplete]);
    useEffect(() => { onSearchErrorRef.current = onSearchError; }, [onSearchError]);
    useEffect(() => { enableDebugCandidatesRef.current = enableDebugCandidates; }, [enableDebugCandidates]);

    const capturePadding = useMemo(() => resolvePaddingObject(padding), [padding]);
    const displayPadding = useMemo(
        () => scalePadding(capturePadding, width, height, displayWidth, displayHeight),
        [capturePadding, width, height, displayWidth, displayHeight]
    );
    const captureFrame = useMemo(
        () => buildFrame(width, height, capturePadding),
        [width, height, capturePadding]
    );
    const displayFrame = useMemo(
        () => buildFrame(displayWidth, displayHeight, displayPadding),
        [displayWidth, displayHeight, displayPadding]
    );

    // Overlay boxes (top-left logo + bottom-right stats legend).
    // Stored in capture pixels so callers can pass sizes that match the final generated
    // image, then scaled to display pixels for both scoring and rendering.
    const captureOverlays = useMemo(() => ({
        logo: {
            x: 0,
            y: 0,
            width: Math.max(0, Number(logoOverlayWidth) || 0),
            height: Math.max(0, Number(logoOverlayHeight) || 0),
        },
        stats: {
            x: Math.max(0, width - (Number(statsOverlayWidth) || 0)),
            y: Math.max(0, height - (Number(statsOverlayHeight) || 0)),
            width: Math.max(0, Number(statsOverlayWidth) || 0),
            height: Math.max(0, Number(statsOverlayHeight) || 0),
        },
    }), [logoOverlayWidth, logoOverlayHeight, statsOverlayWidth, statsOverlayHeight, width, height]);

    const displayOverlays = useMemo(() => {
        const scaleX = Math.max(0.0001, displayWidth) / Math.max(0.0001, width);
        const scaleY = Math.max(0.0001, displayHeight) / Math.max(0.0001, height);
        const toDisplay = (box) => ({
            x: box.x * scaleX,
            y: box.y * scaleY,
            width: box.width * scaleX,
            height: box.height * scaleY,
        });
        return {
            logo: toDisplay(captureOverlays.logo),
            stats: toDisplay(captureOverlays.stats),
        };
    }, [captureOverlays, width, height, displayWidth, displayHeight]);

    const scoreCandidate = useCallback((coords, camera, bearingDeg, frame, displayOverlayBoxes) => {
        if (!camera) return null;

        const frameMinX = frame.padLeft;
        const frameMaxX = frame.width - frame.padRight;
        const frameMinY = frame.padTop;
        const frameMaxY = frame.height - frame.padBottom;

        const logoBox = displayOverlayBoxes?.logo || { x: 0, y: 0, width: 0, height: 0 };
        const statsBox = displayOverlayBoxes?.stats || { x: 0, y: 0, width: 0, height: 0 };
        const insideBox = (point, box) => (
            box.width > 0 && box.height > 0
            && point.x >= box.x && point.x <= box.x + box.width
            && point.y >= box.y && point.y <= box.y + box.height
        );

        let minX = Infinity;
        let maxX = -Infinity;
        let minY = Infinity;
        let maxY = -Infinity;
        let onScreenCount = 0;
        let insidePaddingCount = 0;
        let logoHitCount = 0;
        let statsHitCount = 0;

        coords.forEach((coord) => {
            const point = projectLngLat(
                coord[0],
                coord[1],
                camera.centerLng,
                camera.centerLat,
                camera.zoom,
                bearingDeg,
                frame.width,
                frame.height
            );
            if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) return;
            if (point.x < minX) minX = point.x;
            if (point.x > maxX) maxX = point.x;
            if (point.y < minY) minY = point.y;
            if (point.y > maxY) maxY = point.y;
            if (point.x >= 0 && point.x <= frame.width && point.y >= 0 && point.y <= frame.height) onScreenCount += 1;
            if (point.x >= frameMinX && point.x <= frameMaxX && point.y >= frameMinY && point.y <= frameMaxY) insidePaddingCount += 1;
            if (insideBox(point, logoBox)) logoHitCount += 1;
            if (insideBox(point, statsBox)) statsHitCount += 1;
        });

        if (!Number.isFinite(minX)) return null;

        const safeWidth = Math.max(1, frameMaxX - frameMinX);
        const safeHeight = Math.max(1, frameMaxY - frameMinY);
        const safeCenterX = (frameMinX + frameMaxX) / 2;
        const safeCenterY = (frameMinY + frameMaxY) / 2;
        const bboxWidth = Math.max(1, maxX - minX);
        const bboxHeight = Math.max(1, maxY - minY);
        const bboxCenterX = (minX + maxX) / 2;
        const bboxCenterY = (minY + maxY) / 2;
        const bboxArea = bboxWidth * bboxHeight;

        const overlapMinX = Math.max(minX, frameMinX);
        const overlapMaxX = Math.min(maxX, frameMaxX);
        const overlapMinY = Math.max(minY, frameMinY);
        const overlapMaxY = Math.min(maxY, frameMaxY);
        const overlapWidth = Math.max(0, overlapMaxX - overlapMinX);
        const overlapHeight = Math.max(0, overlapMaxY - overlapMinY);
        const insidePaddingBboxArea = overlapWidth * overlapHeight;
        const outsidePaddingBboxArea = Math.max(0, bboxArea - insidePaddingBboxArea);

        const centerDistanceNormalized = Math.hypot(
            bboxCenterX - safeCenterX,
            bboxCenterY - safeCenterY
        ) / Math.max(1, Math.hypot(frame.width / 2, frame.height / 2));

        const footprintRatio = (bboxWidth / safeWidth) * (bboxHeight / safeHeight);
        const outsidePaddingAreaRatio = outsidePaddingBboxArea / Math.max(1, bboxArea);
        const onScreenRatio = onScreenCount / coords.length;
        const insidePaddingRatio = insidePaddingCount / coords.length;

        const logoHitRatio = coords.length ? logoHitCount / coords.length : 0;
        const statsHitRatio = coords.length ? statsHitCount / coords.length : 0;

        const footprintReward = Math.min(FOOTPRINT_REWARD_CAP, footprintRatio) * FOOTPRINT_REWARD_WEIGHT;
        const centerPenalty = centerDistanceNormalized * CENTER_PENALTY_WEIGHT;
        const outOfBoundsPenalty = outsidePaddingAreaRatio > OUT_OF_BOUNDS_EPSILON ? OUT_OF_BOUNDS_PENALTY : 0;
        const overlayPenalty = (logoHitRatio + statsHitRatio) * OVERLAY_PENALTY_WEIGHT;
        const score = footprintReward - centerPenalty - outOfBoundsPenalty - overlayPenalty;

        return {
            canvasWidth: frame.width,
            canvasHeight: frame.height,
            displayWidth: frame.width,
            displayHeight: frame.height,
            bboxWidth,
            bboxHeight,
            bboxArea,
            bboxCenterX,
            bboxCenterY,
            minX,
            maxX,
            minY,
            maxY,
            bboxWidthRatio: bboxWidth / safeWidth,
            bboxHeightRatio: bboxHeight / safeHeight,
            footprintRatio,
            outsidePaddingAreaRatio,
            onScreenRatio,
            insidePaddingRatio,
            centerDistanceNormalized,
            logoHitCount,
            statsHitCount,
            logoHitRatio,
            statsHitRatio,
            footprintReward,
            coverageReward: 0,
            centerPenalty,
            outOfBoundsPenalty,
            overlayPenalty,
            score,
        };
    }, []);

    const buildThumbnail = useCallback((canvas, metrics, displayPadding, displayOverlayBoxes) => {
        if (!canvas) return null;
        const maxWidth = 220;
        const scale = Math.min(1, maxWidth / canvas.width);
        const targetWidth = Math.max(1, Math.round(canvas.width * scale));
        const targetHeight = Math.max(1, Math.round(canvas.height * scale));
        const thumb = document.createElement('canvas');
        thumb.width = targetWidth;
        thumb.height = targetHeight;
        const ctx = thumb.getContext('2d');
        if (!ctx) return null;
        ctx.drawImage(canvas, 0, 0, targetWidth, targetHeight);
        if (metrics) {
            const scaleX = targetWidth / Math.max(1, metrics.displayWidth);
            const scaleY = targetHeight / Math.max(1, metrics.displayHeight);

            if (displayPadding) {
                const safeX = displayPadding.left * scaleX;
                const safeY = displayPadding.top * scaleY;
                const safeW = targetWidth - (displayPadding.left + displayPadding.right) * scaleX;
                const safeH = targetHeight - (displayPadding.top + displayPadding.bottom) * scaleY;
                ctx.save();
                ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
                ctx.lineWidth = 2;
                ctx.setLineDash([6, 4]);
                ctx.strokeRect(safeX, safeY, safeW, safeH);
                ctx.restore();
            }

            const drawOverlayBox = (box, fill, stroke) => {
                if (!box || box.width <= 0 || box.height <= 0) return;
                ctx.save();
                ctx.fillStyle = fill;
                ctx.strokeStyle = stroke;
                ctx.lineWidth = 1.5;
                ctx.setLineDash([4, 3]);
                ctx.fillRect(box.x * scaleX, box.y * scaleY, box.width * scaleX, box.height * scaleY);
                ctx.strokeRect(box.x * scaleX, box.y * scaleY, box.width * scaleX, box.height * scaleY);
                ctx.restore();
            };
            drawOverlayBox(displayOverlayBoxes?.logo, 'rgba(239, 68, 68, 0.18)', 'rgba(239, 68, 68, 0.9)');
            drawOverlayBox(displayOverlayBoxes?.stats, 'rgba(59, 130, 246, 0.18)', 'rgba(59, 130, 246, 0.9)');
        }
        return thumb.toDataURL('image/jpeg', 0.72);
    }, []);

    const emitCandidate = useCallback((payload) => {
        if (!enableDebugCandidatesRef.current || !onDebugCandidateRef.current || !mapRef.current) return;
        try {
            const thumbnailDataUrl = buildThumbnail(mapRef.current.getCanvas(), payload, payload.adaptivePadding, payload.displayOverlays);
            onDebugCandidateRef.current({
                ...payload,
                id: `coarse-${payload.index}-${Date.now()}-${Math.round(Math.random() * 100000)}`,
                thumbnailDataUrl,
                camera: {
                    center: [mapRef.current.getCenter().lng, mapRef.current.getCenter().lat],
                    zoom: mapRef.current.getZoom(),
                    bearing: normalizeBearing(mapRef.current.getBearing()),
                    pitch: mapRef.current.getPitch(),
                },
            });
        } catch (error) {
            console.error('ActivityMap2DSearch: emit candidate failed', error);
        }
    }, [buildThumbnail]);

    useEffect(() => {
        if (!mapContainer.current || !activity) return;

        const coords = decodeActivityPolyline(activity, selectedSegment);
        if (!coords || coords.length === 0) {
            setHasError(true);
            setProgressText('No route data');
            onSearchErrorRef.current?.(new Error('No valid coordinates for 2D map.'));
            return;
        }

        setHasError(false);
        setProgressText('Loading 2D map...');
        onSearchStartRef.current?.();

        const map = new mapboxgl.Map({
            container: mapContainer.current,
            style: mapStyleUrl,
            center: coords[Math.floor(coords.length / 2)],
            zoom: 10,
            bearing: 0,
            pitch: 0,
            interactive: false,
            attributionControl: false,
            preserveDrawingBuffer: true,
            antialias: true,
        });
        mapRef.current = map;

        let cancelled = false;

        const runSearch = async () => {
            try {
                const worldCoords = coords.map(([lng, lat]) => lngLatToWorld(lng, lat));
                const stepDegrees = 360 / Math.max(1, coarseBearingCount);
                const candidates = [];
                let bestCandidate = null;

                for (let index = 0; index < coarseBearingCount; index += 1) {
                    if (cancelled || !mapRef.current) return;

                    const requestedBearing = normalizeBearing(index * stepDegrees);
                    setProgressText(`Evaluating bearing ${requestedBearing.toFixed(0)}° (${index + 1}/${coarseBearingCount})`);

                    const captureCamera = fitCameraToRoute(worldCoords, requestedBearing, captureFrame);
                    const displayCamera = fitCameraToRoute(worldCoords, requestedBearing, displayFrame);
                    if (!captureCamera || !displayCamera) {
                        console.warn('ActivityMap2DSearch: fitCameraToRoute returned null for bearing', requestedBearing);
                        continue;
                    }

                    map.jumpTo({
                        center: [displayCamera.centerLng, displayCamera.centerLat],
                        zoom: displayCamera.zoom,
                        bearing: requestedBearing,
                        pitch: 0,
                    });

                    await waitForIdle(map);
                    await waitForAnimationFrames(1);

                    const metrics = scoreCandidate(coords, displayCamera, requestedBearing, displayFrame, displayOverlays);
                    if (!metrics) continue;

                    const candidatePayload = {
                        phase: 'coarse',
                        index: index + 1,
                        total: coarseBearingCount,
                        bearing: requestedBearing,
                        pitch: 0,
                        requestedZoom: displayCamera.zoom,
                        requestedCaptureZoom: captureCamera.zoom,
                        captureCamera: {
                            center: [captureCamera.centerLng, captureCamera.centerLat],
                            zoom: captureCamera.zoom,
                            bearing: requestedBearing,
                            pitch: 0,
                        },
                        requestedCamera: {
                            center: [displayCamera.centerLng, displayCamera.centerLat],
                            zoom: displayCamera.zoom,
                            bearing: requestedBearing,
                            pitch: 0,
                        },
                        adaptivePadding: displayPadding,
                        capturePadding,
                        captureOverlays,
                        displayOverlays,
                        ...metrics,
                        visiblePixels: null,
                        visibleSampleCount: null,
                        totalSampleCount: null,
                        visibleSampleRatio: null,
                        internalGapCount: null,
                        internalGapRatio: null,
                    };

                    candidates.push(candidatePayload);
                    emitCandidate(candidatePayload);

                    if (!bestCandidate || candidatePayload.score > bestCandidate.score) {
                        bestCandidate = candidatePayload;
                    }

                    if (stepDelayMs > 0) {
                        await waitForMs(stepDelayMs);
                    }
                }

                if (cancelled || !mapRef.current) return;

                if (bestCandidate) {
                    setProgressText(`Best bearing: ${bestCandidate.bearing.toFixed(0)}° (score ${bestCandidate.score.toFixed(0)})`);
                    const bestCamera = bestCandidate.requestedCamera;
                    if (bestCamera) {
                        map.jumpTo({
                            center: bestCamera.center,
                            zoom: bestCamera.zoom,
                            bearing: bestCandidate.bearing,
                            pitch: 0,
                        });
                        await waitForIdle(map);
                    }
                } else {
                    setProgressText('No viable bearing found');
                }

                const summary = {
                    enabled: true,
                    searchConfig: {
                        coarseBearingCount,
                        coarseBearingStepDegrees: stepDegrees,
                        capturePadding,
                        displayPadding,
                        captureOverlays,
                        displayOverlays,
                        mapStyleUrl,
                        lineWidth,
                        lineColor,
                        lineOpacity,
                        displayWidth,
                        displayHeight,
                        captureWidth: width,
                        captureHeight: height,
                    },
                    candidates,
                    bestCandidate,
                    selectedCamera: bestCandidate ? {
                        bearing: bestCandidate.bearing,
                        pitch: 0,
                        zoom: bestCandidate.requestedZoom,
                        captureZoom: bestCandidate.requestedCaptureZoom,
                        center: bestCandidate.requestedCamera?.center || null,
                        captureCenter: bestCandidate.captureCamera?.center || null,
                    } : null,
                };

                onDebugSummaryRef.current?.(summary);
                onSearchCompleteRef.current?.(summary);
            } catch (error) {
                console.error('ActivityMap2DSearch: search failed', error);
                setHasError(true);
                setProgressText(`Search failed: ${error.message}`);
                onSearchErrorRef.current?.(error);
            }
        };

        map.on('load', () => {
            if (cancelled || !mapRef.current) return;

            const geoJSON = {
                type: 'Feature',
                properties: {},
                geometry: { type: 'LineString', coordinates: coords },
            };
            map.addSource(ROUTE_SOURCE_ID, { type: 'geojson', data: geoJSON });
            map.addLayer({
                id: ROUTE_LAYER_ID,
                type: 'line',
                source: ROUTE_SOURCE_ID,
                layout: { 'line-join': 'round', 'line-cap': 'round' },
                paint: {
                    'line-color': lineColor,
                    'line-width': Math.max(1, Number(lineWidth) || 1),
                    'line-opacity': lineOpacity,
                },
            });

            map.once('idle', () => {
                void runSearch();
            });
        });

        map.on('error', (event) => {
            console.error('ActivityMap2DSearch: map error', event?.error || event);
            setHasError(true);
            setProgressText('Map error');
            onSearchErrorRef.current?.(event?.error || new Error('Map error'));
        });

        return () => {
            cancelled = true;
            if (mapRef.current) {
                try { mapRef.current.remove(); } catch (error) { /* noop */ }
                mapRef.current = null;
            }
        };
    }, [
        activity,
        selectedSegment,
        mapStyleUrl,
        lineWidth,
        lineColor,
        lineOpacity,
        capturePadding,
        displayPadding,
        captureFrame,
        displayFrame,
        captureOverlays,
        displayOverlays,
        coarseBearingCount,
        stepDelayMs,
        displayWidth,
        displayHeight,
        width,
        height,
        scoreCandidate,
        emitCandidate,
    ]);

    return (
        <div style={{ position: 'relative', width: displayWidth, height: displayHeight }}>
            <div
                ref={mapContainer}
                style={{ width: '100%', height: '100%', borderRadius: '8px', overflow: 'hidden' }}
            />
            {/* Padding overlay: dashed rectangle showing the safe frame the search fits the route into */}
            <div
                style={{
                    position: 'absolute',
                    top: displayPadding.top,
                    left: displayPadding.left,
                    right: displayPadding.right,
                    bottom: displayPadding.bottom,
                    border: '2px dashed rgba(255, 255, 255, 0.85)',
                    boxShadow: '0 0 0 2px rgba(17, 24, 39, 0.35)',
                    borderRadius: 4,
                    pointerEvents: 'none',
                }}
            />
            {/* Logo overlay box (top-left) */}
            <div
                style={{
                    position: 'absolute',
                    top: displayOverlays.logo.y,
                    left: displayOverlays.logo.x,
                    width: displayOverlays.logo.width,
                    height: displayOverlays.logo.height,
                    background: 'rgba(239, 68, 68, 0.18)',
                    border: '1.5px dashed rgba(239, 68, 68, 0.95)',
                    pointerEvents: 'none',
                }}
            />
            {/* Stats legend overlay box (bottom-right) */}
            <div
                style={{
                    position: 'absolute',
                    top: displayOverlays.stats.y,
                    left: displayOverlays.stats.x,
                    width: displayOverlays.stats.width,
                    height: displayOverlays.stats.height,
                    background: 'rgba(59, 130, 246, 0.18)',
                    border: '1.5px dashed rgba(59, 130, 246, 0.95)',
                    pointerEvents: 'none',
                }}
            />
            <div
                style={{
                    position: 'absolute',
                    top: 6,
                    left: 6,
                    padding: '3px 6px',
                    background: 'rgba(17, 24, 39, 0.78)',
                    color: '#fff',
                    fontSize: 11,
                    fontWeight: 600,
                    borderRadius: 4,
                    pointerEvents: 'none',
                    letterSpacing: '0.02em',
                }}
            >
                Pad {Math.round(capturePadding.top)}/{Math.round(capturePadding.right)}/{Math.round(capturePadding.bottom)}/{Math.round(capturePadding.left)}px
                {' · '}Logo {Math.round(captureOverlays.logo.width)}×{Math.round(captureOverlays.logo.height)}
                {' · '}Stats {Math.round(captureOverlays.stats.width)}×{Math.round(captureOverlays.stats.height)}
            </div>
            {(progressText || hasError) && (
                <div
                    style={{
                        position: 'absolute',
                        bottom: 8,
                        left: 8,
                        right: 8,
                        padding: '6px 10px',
                        background: hasError ? 'rgba(185, 28, 28, 0.9)' : 'rgba(17, 24, 39, 0.78)',
                        color: '#fff',
                        fontSize: 12,
                        borderRadius: 6,
                        pointerEvents: 'none',
                    }}
                >
                    {progressText}
                </div>
            )}
        </div>
    );
};

export default ActivityMap2DSearch;
